import { css } from 'lit';

/**
 * Shared styles matching The Golden Peptides design (Light Theme)
 * Uses CSS custom properties for theming
 * 
 * Color palette based on The Golden Peptides template:
 * - Background: stone-50 (#fafaf9)
 * - Surfaces: white (#ffffff)
 * - Primary accent: Gold (#b4932a / #d4af37)
 * - Text: stone-900 (#1c1917) for headings, stone-500 (#78716c) for body
 */
export const sharedStyles = css`
  :host {
    /* Light-theme fallbacks — overridden inline by applyThemeTokens / LANDI_STORE_THEME */
    --landi-primary-light: rgba(180, 147, 42, 0.1);
    --landi-surface-elevated: #ffffff;
    --landi-text-secondary: #44403c;
    --landi-text-muted: #78716c;
    --landi-text-light: #a8a29e;
    --landi-border-light: #f5f5f4;
    --landi-button-bg: #1c1917;
    --landi-button-text: #ffffff;
    --landi-button-hover-bg: var(--landi-primary, #b4932a);
    --landi-image-bg: var(--landi-background, #fafaf9);
    --landi-success: #22c55e;
    --landi-success-bg: rgba(34, 197, 94, 0.1);
    --landi-warning: #f59e0b;
    --landi-warning-bg: rgba(245, 158, 11, 0.1);
    --landi-error: #ef4444;
    --landi-error-bg: rgba(239, 68, 68, 0.1);
    --landi-radius-md: 8px;
    --landi-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
    --landi-shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    --landi-shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    --landi-transition: 200ms ease;
    --landi-transition-fast: 150ms ease;
    font-family: var(--landi-font-sans, 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif);
    color: var(--landi-text);
    box-sizing: border-box;
    line-height: 1.5;
  }
  
  *, *::before, *::after {
    box-sizing: inherit;
  }
  
  /* Heading styles using serif font */
  h1, h2, h3, h4, h5, h6,
  .heading {
    font-family: var(--landi-font-serif);
    font-weight: 500;
    line-height: 1.3;
  }
  
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
  
  /* Text utilities */
  .text-gold {
    color: var(--landi-primary);
  }
  
  .text-muted {
    color: var(--landi-text-muted);
  }
  
  .uppercase {
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }
`;

export const buttonStyles = css`
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    border-radius: var(--landi-radius);
    border: none;
    cursor: pointer;
    transition: all var(--landi-transition);
    text-decoration: none;
  }
  
  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  .btn-primary {
    background: var(--landi-button-bg);
    color: var(--landi-button-text);
  }
  
  .btn-primary:hover:not(:disabled) {
    background: var(--landi-button-hover-bg);
    color: var(--landi-button-text);
  }
  
  /* Secondary/Outline button */
  .btn-secondary {
    background: transparent;
    color: var(--landi-text);
    border: 1px solid var(--landi-border);
  }
  
  .btn-secondary:hover:not(:disabled) {
    border-color: var(--landi-primary);
    color: var(--landi-primary);
  }
  
  /* Gold button */
  .btn-gold {
    background: var(--landi-button-bg, var(--landi-accent));
    color: var(--landi-button-text);
  }
  
  .btn-gold:hover:not(:disabled) {
    background: var(--landi-button-hover-bg, var(--landi-primary-hover));
  }
  
  /* Ghost button */
  .btn-ghost {
    background: transparent;
    color: var(--landi-text-muted);
    padding: 0.5rem;
  }
  
  .btn-ghost:hover:not(:disabled) {
    color: var(--landi-primary);
  }
  
  /* Link style button */
  .btn-link {
    background: transparent;
    color: var(--landi-primary);
    padding: 0;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    border-bottom: 1px solid var(--landi-primary);
    border-radius: 0;
  }
  
  .btn-link:hover:not(:disabled) {
    color: var(--landi-primary-hover);
    border-bottom-color: var(--landi-primary-hover);
  }
  
  .btn-sm {
    padding: 0.5rem 1rem;
    font-size: 0.688rem;
  }
  
  .btn-lg {
    padding: 1rem 2.5rem;
    font-size: 0.875rem;
  }
  
  .btn-full {
    width: 100%;
  }
  
  .btn-icon {
    padding: 0.5rem;
    border-radius: 50%;
  }
`;

export const cardStyles = css`
  .card {
    background: var(--landi-surface);
    border-radius: var(--landi-radius);
    overflow: hidden;
    transition: all var(--landi-transition);
    box-shadow: var(--landi-shadow);
  }
  
  .card:hover {
    box-shadow: var(--landi-shadow-lg);
  }
  
  .card-bordered {
    border: 1px solid var(--landi-border);
    box-shadow: none;
  }
  
  .card-bordered:hover {
    border-color: var(--landi-text-light);
    box-shadow: var(--landi-shadow);
  }
  
  .card-image {
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
    background: var(--landi-image-bg, var(--landi-background));
  }
  
  .card-image-wrapper {
    position: relative;
    overflow: hidden;
  }
  
  .card-image-wrapper img {
    transition: transform 700ms ease;
  }
  
  .card:hover .card-image-wrapper img {
    transform: scale(1.05);
  }
  
  .card-body {
    padding: 1rem;
  }
  
  .card-title {
    font-family: var(--landi-font-serif);
    font-size: 1.125rem;
    font-weight: 500;
    margin: 0 0 0.5rem;
    color: var(--landi-text);
    transition: color var(--landi-transition);
  }
  
  .card:hover .card-title {
    color: var(--landi-primary);
  }
  
  .card-subtitle {
    font-size: 0.875rem;
    color: var(--landi-text-muted);
    margin: 0 0 0.5rem;
    font-weight: 300;
    line-height: 1.6;
  }
`;

export const inputStyles = css`
  .input {
    width: 100%;
    padding: 0.75rem 1rem;
    font-size: 1rem;
    font-family: var(--landi-font-sans);
    background: var(--landi-surface);
    border: 1px solid var(--landi-border);
    border-radius: var(--landi-radius-md);
    color: var(--landi-text);
    transition: border-color var(--landi-transition), box-shadow var(--landi-transition);
  }
  
  .input:focus {
    outline: none;
    border-color: var(--landi-primary);
    box-shadow: 0 0 0 3px var(--landi-primary-light);
  }
  
  .input::placeholder {
    color: var(--landi-text-light);
  }
  
  .input-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .input-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--landi-text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  .input-helper {
    font-size: 0.813rem;
    color: var(--landi-text-muted);
  }
  
  .input-error {
    border-color: var(--landi-error);
  }
  
  .input-error:focus {
    box-shadow: 0 0 0 3px var(--landi-error-bg);
  }
  
  .error-text {
    font-size: 0.75rem;
    color: var(--landi-error);
  }
  
  /* Select styles */
  .select {
    appearance: none;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%2378716c' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
    background-position: right 0.75rem center;
    background-repeat: no-repeat;
    background-size: 1.25rem;
    padding-right: 2.5rem;
  }
  
  /* Textarea */
  .textarea {
    min-height: 120px;
    resize: vertical;
  }
`;

export const badgeStyles = css`
  .badge {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    font-size: 0.688rem;
    font-weight: 700;
    border-radius: var(--landi-radius);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  .badge-success {
    background: var(--landi-success-bg);
    color: var(--landi-success);
  }
  
  .badge-warning {
    background: var(--landi-warning-bg);
    color: var(--landi-warning);
  }
  
  .badge-error {
    background: var(--landi-error-bg);
    color: var(--landi-error);
  }
  
  .badge-primary {
    background: var(--landi-primary-light);
    color: var(--landi-primary);
  }
  
  /* Solid badges */
  .badge-solid {
    background: var(--landi-text);
    color: var(--landi-surface);
  }
  
  .badge-gold {
    background: var(--landi-button-bg, var(--landi-primary));
    color: var(--landi-button-text);
  }
  
  /* Status indicator dots */
  .status-dot {
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    display: inline-block;
  }
  
  .status-dot-success {
    background: var(--landi-success);
  }
  
  .status-dot-warning {
    background: var(--landi-warning);
  }
  
  .status-dot-error {
    background: var(--landi-error);
  }
`;

/**
 * Layout utilities
 */
export const layoutStyles = css`
  .container {
    max-width: 80rem;
    margin: 0 auto;
    padding: 0 1.5rem;
  }
  
  @media (min-width: 640px) {
    .container {
      padding: 0 3rem;
    }
  }
  
  .section {
    padding: 4rem 0;
  }
  
  .section-lg {
    padding: 6rem 0;
  }
  
  /* Grid layouts */
  .grid {
    display: grid;
    gap: 1.5rem;
  }
  
  .grid-cols-2 {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .grid-cols-3 {
    grid-template-columns: repeat(3, 1fr);
  }
  
  .grid-cols-4 {
    grid-template-columns: repeat(4, 1fr);
  }
  
  @media (max-width: 1024px) {
    .grid-cols-4 {
      grid-template-columns: repeat(3, 1fr);
    }
  }
  
  @media (max-width: 768px) {
    .grid-cols-3,
    .grid-cols-4 {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  
  @media (max-width: 480px) {
    .grid-cols-2,
    .grid-cols-3,
    .grid-cols-4 {
      grid-template-columns: 1fr;
    }
  }
  
  /* Flex utilities */
  .flex {
    display: flex;
  }
  
  .flex-col {
    flex-direction: column;
  }
  
  .items-center {
    align-items: center;
  }
  
  .justify-between {
    justify-content: space-between;
  }
  
  .justify-center {
    justify-content: center;
  }
  
  .gap-2 {
    gap: 0.5rem;
  }
  
  .gap-4 {
    gap: 1rem;
  }
  
  .gap-6 {
    gap: 1.5rem;
  }
  
  .gap-8 {
    gap: 2rem;
  }
`;

/**
 * Typography utilities
 */
export const typographyStyles = css`
  .section-label {
    color: var(--landi-primary);
    font-weight: 700;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.2em;
    margin-bottom: 1rem;
  }
  
  .section-title {
    font-family: var(--landi-font-serif);
    font-size: 2.5rem;
    font-weight: 500;
    color: var(--landi-text);
    line-height: 1.2;
    margin-bottom: 1.5rem;
  }
  
  @media (min-width: 1024px) {
    .section-title {
      font-size: 3rem;
    }
  }
  
  .section-subtitle {
    font-size: 1rem;
    color: var(--landi-text-muted);
    font-weight: 300;
    max-width: 40rem;
  }
  
  .price {
    font-weight: 700;
    color: var(--landi-text);
  }
  
  .price-lg {
    font-size: 1.5rem;
  }
  
  .price-compare {
    font-size: 0.875rem;
    color: var(--landi-text-muted);
    text-decoration: line-through;
    font-weight: 400;
  }
  
  .price-gold {
    color: var(--landi-primary);
  }
`;
